// Sign-in through an external provider (Google, Strava), linked accounts and uploading activities to Strava.
import { z } from 'zod/v4';

export const OAUTH_PROVIDERS = ['google', 'strava'] as const;
export const OAuthProvider = z.enum(OAUTH_PROVIDERS);
export type OAuthProvider = z.infer<typeof OAuthProvider>;
export const OAuthProviderParam = z.object({ provider: OAuthProvider });

/** Providers configured on this server (absent buttons are not shown). */
export const ProvidersResponse = z.object({ google: z.boolean(), strava: z.boolean() });
export type ProvidersResponse = z.infer<typeof ProvidersResponse>;

/**
 * `nonce`: random secret drawn by the browser and kept in sessionStorage. It is required to finish the flow:
 * a stolen or forced return link (sign-in or linking CSRF) is useless without it.
 */
const Nonce = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/);
/** `app`: application to return to. The admin console (ADR 0007) only signs in, never links. */
export const OAUTH_APPS = ['game', 'admin'] as const;
export const OAuthStartBody = z
  .object({ intent: z.enum(['login', 'link']), nonce: Nonce, app: z.enum(OAUTH_APPS).default('game') })
  .refine(b => b.app === 'game' || b.intent === 'login', { message: 'The console does not link accounts', path: ['intent'] });
export type OAuthStartBody = z.infer<typeof OAuthStartBody>;
export const OAuthStartResponse = z.object({ url: z.string().url() });

/** Query of the provider's callback: either `code` + `state`, or `error`. */
export const OAuthCallbackQuery = z.object({
  code: z.string().min(1).max(2048).optional(),
  state: z.string().min(1).max(200).optional(),
  error: z.string().max(200).optional(),
  /** Strava returns here the permissions actually granted (the player can untick some). */
  scope: z.string().max(500).optional(),
});

/** Failure reasons passed to the application in the return URL (#/signin/<provider>/error/<reason>). */
export const OAUTH_ERRORS = ['denied', 'expired', 'provider'] as const;
export type OAuthErrorReason = (typeof OAUTH_ERRORS)[number];

export const OAuthFinishBody = z.object({ code: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/), nonce: Nonce });

export const LinkedAccount = z.object({
  provider: OAuthProvider,
  /** Name shown at the provider (Strava first name, Google name). */
  name: z.string(),
  email: z.string().nullable(),
  linkedAt: z.number(),
  /** Strava: uploading activities is allowed ("activity:write" permission granted). */
  canUpload: z.boolean(),
  /** Strava: every new activity is uploaded automatically. */
  autoUpload: z.boolean(),
});
export type LinkedAccount = z.infer<typeof LinkedAccount>;
export const AccountsResponse = z.object({ hasPassword: z.boolean(), linked: z.array(LinkedAccount) });
export type AccountsResponse = z.infer<typeof AccountsResponse>;
export const StravaSettingsBody = z.object({ autoUpload: z.boolean() });

export const StravaUpload = z.object({
  status: z.enum(['none', 'pending', 'done', 'error']),
  /** Numeric id of the activity at Strava (for the link). */
  stravaActivityId: z.string().regex(/^\d{1,20}$/).nullable(),
  error: z.string().max(300).nullable(),
});
export type StravaUpload = z.infer<typeof StravaUpload>;
export const ActivityIdParam = z.object({ id: z.string().min(1).max(40) });

// ---------- Strava webhook (developers.strava.com/docs/webhooks) ----------
/** Subscription check: Strava calls the callback with these and expects `{"hub.challenge": <the same value>}` back. */
export const StravaWebhookVerifyQuery = z.object({
  'hub.mode': z.literal('subscribe'),
  'hub.challenge': z.string().min(1).max(200),
  'hub.verify_token': z.string().min(1).max(200),
});
/** An event pushed by Strava. The only one acted upon is the athlete revoking the application (`updates.authorized = "false"`). */
export const StravaWebhookEvent = z.object({
  object_type: z.enum(['activity', 'athlete']),
  object_id: z.number().int().nonnegative(),
  aspect_type: z.enum(['create', 'update', 'delete']),
  owner_id: z.number().int().nonnegative(),
  subscription_id: z.number().int(),
  event_time: z.number().int(),
  updates: z.record(z.string().max(50), z.union([z.string().max(500), z.boolean()])).default({}),
});
export type StravaWebhookEvent = z.infer<typeof StravaWebhookEvent>;
export const revokesAccess = (e: StravaWebhookEvent) => e.object_type === 'athlete' && e.aspect_type === 'update' && String(e.updates.authorized) === 'false';
